/*
 * Humbler stack builder (sections/humbler-stack-builder.liquid, docs/PENDANT-STACK-BUILDER-PLAN.md): finish x length
 * pair x shorter chain x longer chain picks one of 80 pre-rendered model photos; the pendant is drawn on a canvas at
 * the longer chain's lowest point (the same fit as the pendant stack demo). The next photo is loaded and decoded
 * before it replaces the one on show (no blank stage); only the chains that changed then come into focus (an instant
 * swap with reduced motion), and the photos one change away are fetched when idle.
 * The canvas is only drawn on, never read, so the CDN's cross-origin images need no CORS.
 * Add to bag: the stack's three variants (the section's pendant and chain blocks) go to HumblerCart.add as one set
 * with the live set properties (docs/REBUILD-PLAN.md §6), cartItems() below.
 * Pure helpers are exported (HumblerStackBuilder / module.exports) for tests/stack-builder.test.mjs.
 */
(function (root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module && module.exports) module.exports = api;
  else if (root) root.HumblerStackBuilder = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function (win) {
  'use strict';

  const FIELDS = ['fin', 'len', 'short', 'long', 'pendant'];
  const SIZE = 1200; // canvas width; anchors and pendant sizes are fractions of the photo width
  const SWAP_MS = 380; // a pick's whole change: the new chain comes into focus as the old one fades

  /* ---------- state ---------- */
  const parseDef = text => Object.fromEntries(FIELDS.map((field, i) => [field, String(text || '').split('|')[i] || '']));
  const photoKey = st => `${st.fin}|${st.len}|${st.short}:${st.long}`;

  // A state the data can show: a combination without a photo falls back field by field to the defaults (longer chain
  // first); a pendant without a cut-out in that finish (an unknown key, a design made in the other finish only) to
  // the default pendant, else the first with one. A stack always has a pendant.
  function settle(st, data, def) {
    const out = { ...st };
    for (const field of ['long', 'short', 'len', 'fin']) {
      if (data.photos[photoKey(out)]) break;
      out[field] = def[field];
    }
    if (!data.photos[photoKey(out)]) Object.assign(out, { fin: def.fin, len: def.len, short: def.short, long: def.long });
    if (!data.pendants[`${out.fin}|${out.pendant}`]) {
      const keys = Object.keys(data.pendants).filter(id => id.startsWith(`${out.fin}|`)).map(id => id.slice(out.fin.length + 1));
      out.pendant = keys.includes(def.pendant) ? def.pendant : keys[0] || def.pendant;
    }
    return out;
  }

  function parseState(search, data, def) {
    const q = new URLSearchParams(search || '');
    const st = { ...def };
    for (const field of FIELDS) {
      const value = q.get(`sb_${field}`);
      if (value) st[field] = value;
    }
    return settle(st, data, def);
  }

  // The page's query with this state's sb_* parameters; every other parameter (preview_theme_id, view, utm_*) is kept.
  function stateQuery(st, search) {
    const q = new URLSearchParams(search || '');
    for (const field of FIELDS) q.delete(`sb_${field}`);
    for (const field of FIELDS) q.set(`sb_${field}`, st[field]);
    const text = q.toString();
    return text ? `?${text}` : '';
  }

  // Photo keys one change away (finish, lengths, shorter or longer chain): what a single tap can show next.
  function neighbours(st, data) {
    const here = [st.fin, st.len, st.short, st.long];
    return Object.keys(data.photos).filter(key => {
      const [fin, len, pair] = key.split('|');
      const [short, long] = pair.split(':');
      return [fin, len, short, long].filter((value, i) => value !== here[i]).length === 1;
    });
  }

  // The srcset candidate a browser takes for this CSS width: the smallest that covers it, else the largest.
  const pickWidth = (px, widths) => widths.find(width => width >= px) || widths[widths.length - 1];
  const fill = (text, values) => String(text).replace(/%(\w+)%/g, (match, name) => (name in values ? values[name] : match));

  // The stack's price (sections/humbler-stack-builder.liquid, data-sb-prices): each piece at its own price in the
  // shopper's currency (a chain at its length's variant), against the set price, which is in USD and converted by
  // fromUsd. The set is the pendant and both chains. null = not known.
  function quote(st, prices, fromUsd = cents => cents) {
    const [a, b] = String(st.len).split('-');
    const known = value => (Number.isFinite(value) ? value : null);
    const pendant = known(prices.pendants[`${st.fin}|${st.pendant}`]);
    const short = known(prices.chains[`${st.fin}|${st.short}|${a}`]);
    const long = known(prices.chains[`${st.fin}|${st.long}|${b}`]);
    const parts = [pendant, short, long];
    const separate = parts.every(value => value !== null) ? parts.reduce((sum, value) => sum + value, 0) : null;
    const set = prices.set > 0 ? fromUsd(prices.set) : null;
    const save = separate !== null && set !== null && separate > set ? separate - set : 0;
    return { pendant, short, long, separate, set, save };
  }

  // The stack's variants (sections/humbler-stack-builder.liquid, data-sb-prices "variants": the pendant product as sold,
  // each chain at its length's variant): null for a piece the blocks do not name.
  function variantsFor(st, prices) {
    const [a, b] = String(st.len).split('-');
    const v = prices.variants || {};
    return {
      pendant: v[`${st.fin}|${st.pendant}`] || null,
      short: v[`${st.fin}|${st.short}|${a}`] || null,
      long: v[`${st.fin}|${st.long}|${b}`] || null
    };
  }

  // The bag lines for a stack, or null when a piece is missing or sold out: one line per piece, as one set the shopper
  // built (the live properties: a _bundle_id shared by the lines, _bundle_offer, _bundle_item "1 of 3"), so the store's
  // functions and the bag keep them together. The id starts with "stack-": the store's "Pendant stack $95" discount
  // (scripts/supaeasy/pendant-stack-95.js) prices only such sets.
  function cartItems(st, prices, uuid) {
    const v = variantsFor(st, prices);
    const pieces = [v.pendant, v.short, v.long];
    if (pieces.some(piece => !piece || !piece.id || piece.available === false)) return null;
    const id = `stack-${uuid()}`;
    return pieces.map((piece, i) => ({
      id: Number(piece.id), quantity: 1, handle: piece.handle || '',
      properties: { _bundle_id: id, _bundle_offer: prices.offer || 'Pendant stack', _bundle_item: `${i + 1} of ${pieces.length}` }
    }));
  }

  // The stage's CSS width, as the stylesheet sets it (assets/humbler-stack-builder.css): the photo's `sizes`.
  function stageWidth(viewport, header) {
    const { width, height } = viewport;
    if (width >= 1000) return Math.round(Math.min(720, (height - header - 80) * 0.75, width - 560));
    return width >= 600 ? 480 : Math.round(width);
  }

  /* ---------- pendant fit (the demo's registration: three vertical bands, framed flips get their own X map) ---------- */
  function registeredPendant(layer, g, doc) {
    const sourceW = g.sourceDimensions[0], sourceH = g.sourceDimensions[1];
    const body = g.body, t = g.target;
    const thread = g.thread[1] * layer.height / sourceH;
    const join = body.top * layer.height / sourceH;
    const top = g.alphaTop * layer.height / sourceH;
    const sx = t.width * SIZE / (body.width * layer.width / sourceW);
    const bodyX = body.center[0] * layer.width / sourceW;
    const syTop = t.topDepth * SIZE / (thread - top);
    const syMiddle = t.topOffset * SIZE / (join - thread);
    const sharedOuter = Number.isFinite(t.outerHeight) && Number.isFinite(g.alphaBottom) && g.alphaBottom > body.top;
    const syBody = sharedOuter
      ? (t.outerHeight - t.topDepth - t.topOffset) * SIZE / ((g.alphaBottom - body.top) * layer.height / sourceH)
      : t.height * SIZE / (body.height * layer.height / sourceH);
    const yAt = y => (y <= thread ? (y - thread) * syTop : y <= join ? (y - thread) * syMiddle : t.topOffset * SIZE + (y - join) * syBody);
    let xBounds = [0, layer.width], xScales = [sx], xAt = x => (x - bodyX) * sx;
    const full = g.fullAlpha128Bounds;
    if (g.framedFlip && Number.isFinite(t.outerWidth) && t.outerWidth > t.width && Array.isArray(full)) {
      const l = (body.center[0] - body.width / 2) * layer.width / sourceW;
      const r = (body.center[0] + body.width / 2) * layer.width / sourceW;
      const fl = full[0] * layer.width / sourceW, fr = full[2] * layer.width / sourceW;
      const half = t.width * SIZE / 2, outer = t.outerWidth * SIZE / 2;
      if (l > fl && r < fr) {
        const leftScale = (outer - half) / (l - fl), rightScale = (outer - half) / (fr - r);
        xBounds = [0, l, r, layer.width]; xScales = [leftScale, sx, rightScale];
        xAt = x => (x <= l ? -half + (x - l) * leftScale : x <= r ? -half + (x - l) * sx : half + (x - r) * rightScale);
      }
    }
    const x0 = Math.floor(xAt(0)), y0 = Math.floor(yAt(0));
    const canvas = doc.createElement('canvas');
    canvas.width = Math.ceil(xAt(layer.width)) - x0;
    canvas.height = Math.ceil(yAt(layer.height)) - y0;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    const bounds = [0, thread, join, layer.height], scales = [syTop, syMiddle, syBody];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < xScales.length; j++) {
        const from = i ? Math.round(yAt(bounds[i]) - y0) : 0;
        const to = i === 2 ? canvas.height : Math.round(yAt(bounds[i + 1]) - y0);
        const left = j ? Math.round(xAt(xBounds[j]) - x0) : 0;
        const right = j === xScales.length - 1 ? canvas.width : Math.round(xAt(xBounds[j + 1]) - x0);
        ctx.save(); ctx.beginPath(); ctx.rect(left, from, right - left, to - from); ctx.clip();
        ctx.drawImage(layer, xAt(xBounds[j]) - xBounds[j] * xScales[j] - x0, yAt(bounds[i]) - bounds[i] * scales[i] - y0, layer.width * xScales[j], layer.height * scales[i]);
        ctx.restore();
      }
    }
    return { canvas, pivot: [-x0, -y0] };
  }

  const api = { FIELDS, SIZE, parseDef, photoKey, settle, parseState, stateQuery, neighbours, pickWidth, fill, quote, variantsFor, cartItems, stageWidth, registeredPendant };
  if (!win || !win.customElements || !win.HTMLElement || win.customElements.get('humbler-stack-builder')) return api;

  const idle = fn => (win.requestIdleCallback ? win.requestIdleCallback(fn, { timeout: 1500 }) : win.setTimeout(fn, 300));
  // A set's _bundle_id, as Set Studio makes it (assets/humbler-set-studio.js).
  const uuid = () => (win.crypto?.randomUUID ? win.crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => ((Math.random() * 16) | (c === 'x' ? 0 : 8)).toString(16).slice(-1)));
  const ready = img => (img.complete && img.naturalWidth ? Promise.resolve() : img.decode ? img.decode() : new Promise((resolve, reject) => {
    img.addEventListener('load', resolve, { once: true });
    img.addEventListener('error', reject, { once: true });
  }));

  class StackBuilder extends win.HTMLElement {
    connectedCallback() {
      if (this.started) return;
      try {
        this.data = JSON.parse(this.querySelector('[data-sb-data]').textContent);
        this.s = JSON.parse(this.querySelector('[data-sb-strings]').textContent);
        this.prices = JSON.parse(this.querySelector('[data-sb-prices]')?.textContent || '{}');
      } catch {
        return;
      }
      this.prices = { set: 0, pendants: {}, chains: {}, finishes: {}, variants: {}, ...this.prices };
      this.started = true;
      this.mini = this.hasAttribute('data-mini'); // the product page's compact builder (sections/humbler-product-stack)
      this.def = parseDef(this.dataset.def);
      this.state = parseState(win.location.search, this.data, this.def);
      this.stage = this.querySelector('[data-sb-stage]');
      [this.front, this.back] = this.querySelectorAll('[data-sb-photo]');
      this.canvas = this.querySelector('[data-sb-canvas]');
      this.ctx = this.canvas.getContext('2d');
      this.media = this.querySelector('[data-sb-media]');
      this.buy = this.querySelector('[data-sb-buy]');
      this.bar = this.querySelector('[data-sb-bar]');
      this.layers = new Map();
      this.fetched = new Set();
      this.anims = [];
      this.token = 0;
      this.onChange = event => {
        const input = event.target.closest('[data-sb-input]');
        if (!input) return;
        this.state = settle({ ...this.state, [input.dataset.sbInput]: input.value }, this.data, this.def);
        this.apply();
      };
      this.onClick = event => {
        if (event.target.closest('[data-sb-add]')) this.add();
        else if (event.target.closest('[data-sb-share]')) this.share();
        else if (event.target.closest('[data-sb-expand]')) this.expand();
      };
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        win.requestAnimationFrame(() => { this.ticking = false; this.updateBar(); });
      };
      this.onResize = () => { this.setSizes(); this.updateBar(); };
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      win.addEventListener('scroll', this.onScroll, { passive: true });
      win.addEventListener('resize', this.onResize, { passive: true });
      this.setSizes();
      this.apply({ push: false });
    }

    disconnectedCallback() {
      if (!this.started) return;
      this.started = false;
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      win.removeEventListener('scroll', this.onScroll);
      win.removeEventListener('resize', this.onResize);
      if (this.bar) {
        this.classList.remove('has-bar');
        win.document.documentElement.classList.remove('hb-sb-bar-on');
      }
    }

    header() {
      return parseFloat(win.getComputedStyle(this).getPropertyValue('--hb-header-height')) || 56;
    }

    // Both photos get the stage's real width as `sizes`, so the browser and the idle preloads pick the same file.
    setSizes() {
      this.width = this.mini ? Math.round(this.stage.getBoundingClientRect().width) || 400 : stageWidth({ width: win.innerWidth, height: win.innerHeight }, this.header());
      for (const img of [this.front, this.back]) img.sizes = `${this.width}px`;
    }

    label(field, value) {
      const input = [...this.querySelectorAll(`[data-sb-input="${field}"]`)].find(el => el.value === value);
      return input ? input.dataset.sbLabel : value;
    }

    apply({ push = true } = {}) {
      const st = this.state;
      this.dataset.finish = st.fin;
      this.dataset.len = st.len; // the phone close-up looks lower for the 22″ + 24″ pair (CSS)
      // a design without a cut-out in this finish (made in one finish only) stays in the grid, disabled
      for (const tile of this.querySelectorAll('[data-sb-only]')) tile.querySelector('[data-sb-input]').disabled = tile.dataset.sbOnly !== st.fin;
      for (const input of this.querySelectorAll('[data-sb-input]')) input.checked = input.value === st[input.dataset.sbInput];
      for (const el of this.querySelectorAll('[data-sb-value]')) el.textContent = this.label(el.dataset.sbValue, st[el.dataset.sbValue]);
      const len = [...this.querySelectorAll('[data-sb-input="len"]')].find(el => el.value === st.len);
      const lengths = { short: len?.dataset.sbShort || '', long: len?.dataset.sbLong || '' };
      for (const el of this.querySelectorAll('[data-sb-len]')) el.textContent = lengths[el.dataset.sbLen];
      const names = { short: this.label('short', st.short), long: this.label('long', st.long) };
      const pendant = this.label('pendant', st.pendant);
      this.captionText = fill(this.s.caption, {
        finish: this.s.finishes[st.fin], short_length: lengths.short, short: names.short, long_length: lengths.long, long: names.long, pendant
      });
      for (const el of this.querySelectorAll('[data-sb-caption], [data-sb-live]')) el.textContent = this.captionText;
      const q = quote(st, this.prices, cents => this.setPrice(cents));
      this.renderPrice(q, { lengths, names, pendant });
      this.renderAdd(q);
      // The builder page keeps the stack in its address; the product page's mini builder leaves the product's
      // address alone and carries the picks in its "open the full builder" link instead.
      if (push && !this.mini) win.history.replaceState(win.history.state, '', `${win.location.pathname}${stateQuery(st, win.location.search)}${win.location.hash}`);
      for (const link of this.querySelectorAll('[data-sb-link]')) link.href = `${link.dataset.sbLink}${stateQuery(st, '')}`;
      this.show();
      this.updateBar();
    }

    // The add buttons (under the picks and the phone bar): Add to cart over the saving ("Save $80"), the price when
    // there is no saving, or Sold out when a piece cannot be sold.
    renderAdd(q) {
      const st = this.state, items = cartItems(st, this.prices, () => ''), money = cents => this.ui().money(cents);
      const main = q.set !== null ? q.set : q.separate;
      const sub = q.save > 0 ? fill(this.s.save, { amount: money(q.save) }) : main === null ? '' : money(main);
      for (const button of this.querySelectorAll('[data-sb-add]')) {
        button.disabled = !items || this.adding;
        const label = button.querySelector('[data-sb-add-label]');
        if (label) label.textContent = items ? this.s.add : this.s.sold_out;
        const line = button.querySelector('[data-sb-add-sub]');
        if (line) {
          line.textContent = sub;
          line.hidden = !items || !sub;
        }
      }
    }

    // Adds the stack through the bag (window.HumblerCart, assets/humbler-cart.js), which opens on success.
    async add() {
      if (this.adding || !win.HumblerCart) return;
      const items = cartItems(this.state, this.prices, uuid);
      const status = this.querySelector('[data-sb-add-status]');
      if (!items) return;
      this.adding = true;
      const buttons = [...this.querySelectorAll('[data-sb-add]')];
      for (const button of buttons) { button.disabled = true; button.classList.add('is-loading'); }
      status.classList.remove('is-error');
      status.textContent = this.s.adding;
      let result;
      try {
        result = await win.HumblerCart.add(items, { open: true, source: 'stack' });
      } catch (error) {
        result = { ok: false, error: error && error.message };
      }
      this.adding = false;
      for (const button of buttons) button.classList.remove('is-loading');
      status.classList.toggle('is-error', !result || !result.ok);
      status.textContent = result && result.ok ? this.s.added : (result && result.error) || this.s.add_failed;
      this.renderAdd(quote(this.state, this.prices, cents => this.setPrice(cents)));
      win.clearTimeout(this.addTimer);
      if (result && result.ok) this.addTimer = win.setTimeout(() => { status.textContent = ''; }, 4000);
    }

    // Money in the shopper's currency (assets/humbler-header.js); a plain USD format when that script is absent.
    ui() {
      return win.HumblerUI || { money: cents => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`, fromUsd: cents => cents };
    }

    // The set price in the shopper's currency: in another currency rounded to whole units, a price to read (the bag
    // and checkout keep the exact conversion, a few hundredths apart at most).
    setPrice(centsUsd) {
      const ui = this.ui();
      return ui.fromUsdWhole ? ui.fromUsdWhole(centsUsd) : ui.fromUsd(centsUsd);
    }

    // The price under the title, the one over the add button, and the receipt (each piece with its photo and own price,
    // then the totals).
    renderPrice(q, { lengths, names, pendant }) {
      const st = this.state, money = cents => this.ui().money(cents), doc = win.document;
      const text = (selector, value) => { const el = this.querySelector(selector); if (el) el.textContent = value; };
      const show = (selector, on) => { const el = this.querySelector(selector); if (el) el.hidden = !on; };
      const main = q.set !== null ? q.set : q.separate;
      for (const selector of ['[data-sb-price-now]', '[data-sb-buy-now]']) text(selector, main === null ? '' : money(main));
      show('[data-sb-buy-price]', main !== null);
      text('[data-sb-buy-was-amount]', q.save ? money(q.separate) : '');
      show('[data-sb-buy-was]', q.save > 0);
      text('[data-sb-buy-save]', fill(this.s.you_save, { amount: money(q.save) }));
      show('[data-sb-buy-save]', q.save > 0);
      text('[data-sb-price-was-amount]', q.save ? money(q.separate) : '');
      show('[data-sb-price-was]', q.save > 0);
      text('[data-sb-price-save]', fill(this.s.save, { amount: money(q.save) }));
      show('[data-sb-price-save]', q.save > 0);
      text('[data-sb-price-note]', q.set !== null ? this.s.set_note : '');
      // receipt rows: the photo is the tile's own (this finish)
      const face = (field, value) => {
        const tile = [...this.querySelectorAll(`[data-sb-input="${field}"]`)].find(el => el.value === value)?.closest('label');
        const img = tile && (tile.querySelector(`.hb-sb__face--${st.fin}`) || tile.querySelector('.hb-sb__face'));
        return img ? img.currentSrc || img.src : '';
      };
      const row = (src, name, sub, amount) => {
        const li = doc.createElement('li');
        li.className = 'hb-sb__line';
        li.innerHTML = '<span class="hb-sb__line-media" aria-hidden="true"><img width="40" height="40" alt=""></span><span class="hb-sb__line-copy"><span class="hb-sb__line-name"></span><span class="hb-sb__line-sub"></span></span><span class="hb-sb__line-amount"></span>';
        if (src) li.querySelector('img').src = src;
        else li.querySelector('img').remove();
        li.querySelector('.hb-sb__line-name').textContent = name;
        li.querySelector('.hb-sb__line-sub').textContent = sub;
        li.querySelector('.hb-sb__line-amount').textContent = amount === null ? '' : money(amount);
        return li;
      };
      const chainSub = this.s.chain_sub[st.fin] || '';
      // the pendant product's own finish when the store sells it as another (Death Smile, Spartan Skull: Black)
      const pendantFinish = this.prices.finishes[`${st.fin}|${st.pendant}`] || st.fin;
      const lines = this.querySelector('[data-sb-lines]'); // the builder page's receipt; the mini builder has none
      if (lines) lines.replaceChildren(
        row(face('pendant', st.pendant), pendant, fill(this.s.pendant_sub, { finish: this.s.finishes[pendantFinish] || this.s.finishes[st.fin] }), q.pendant),
        row(face('short', st.short), fill(this.s.chain_name, { chain: names.short }), fill(chainSub, { length: lengths.short }), q.short),
        row(face('long', st.long), fill(this.s.chain_name, { chain: names.long }), fill(chainSub, { length: lengths.long }), q.long)
      );
      show('[data-sb-totals]', main !== null);
      show('[data-sb-total-separate-row]', q.set !== null && q.separate !== null);
      text('[data-sb-total-separate]', q.separate === null ? '' : money(q.separate));
      text('[data-sb-total-label]', q.set !== null ? this.s.total_set : this.s.total);
      text('[data-sb-total-set]', main === null ? '' : money(main));
      text('[data-sb-saving]', fill(this.s.you_save, { amount: money(q.save) }));
      show('[data-sb-saving]', q.save > 0);
    }

    srcset(key) {
      return this.data.photos[key].map((url, i) => `${url} ${this.data.widths[i]}w`).join(', ');
    }

    pendantLayer(fin, key) {
      const id = `${fin}|${key}`;
      if (!this.layers.has(id)) {
        const rec = this.data.pendants[id];
        this.layers.set(id, new Promise((resolve, reject) => {
          const img = new win.Image();
          img.decoding = 'async';
          img.onload = () => resolve(registeredPendant(img, rec.geometry, win.document));
          img.onerror = reject;
          img.src = rec.src;
        }));
      }
      return this.layers.get(id);
    }

    async show() {
      const st = this.state, key = photoKey(st), token = ++this.token;
      const layer = this.pendantLayer(st.fin, st.pendant).catch(() => null);
      const slow = win.setTimeout(() => this.setStatus(this.s.loading), 300);
      let next = this.front;
      try {
        if (this.front.dataset.key !== key) {
          this.stopMotion(); // the photo under a running drape is about to be reused
          next = this.back;
          next.srcset = this.srcset(key);
          next.dataset.key = key;
        }
        await ready(next);
      } catch {
        win.clearTimeout(slow);
        if (token === this.token) this.setStatus(this.s.error);
        return;
      }
      const reg = await layer;
      win.clearTimeout(slow);
      if (token !== this.token) return;
      // The first photo simply appears; later picks come into focus (an instant swap with reduced motion).
      const motion = this.drawn !== undefined && this.motionOk();
      this.stopMotion();
      const old = this.front, swapped = next !== old;
      if (swapped) {
        next.classList.add('is-front');
        old.classList.remove('is-front');
        old.alt = '';
        old.setAttribute('aria-hidden', 'true');
        next.removeAttribute('aria-hidden');
        [this.front, this.back] = [next, old];
        if (motion) this.dissolve(next, old);
      }
      this.front.alt = fill(this.s.alt, { caption: this.captionText });
      // The pendant is redrawn only when it changes: design, finish or length pair (its anchor).
      const drawn = reg ? `${st.fin}|${st.pendant}|${st.len}` : '';
      if (drawn !== this.drawn) {
        if (motion) this.dissolvePendant(reg);
        else this.draw(reg);
        this.drawn = drawn;
      }
      this.setStatus('');
      idle(() => this.prefetch());
    }

    /* ---------- swap motion: only the chains differ between photos, so only they move. The new chain comes into
       focus where the old one was while the old one fades under it; a changed pendant does the same. It starts with
       the pick and is over in SWAP_MS. Web Animations only; none with reduced motion. ---------- */
    motionOk() {
      return typeof this.canvas.animate === 'function' && !(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    play(el, keyframes, options) {
      const anim = el.animate(keyframes, options);
      this.anims.push(anim);
      return anim;
    }

    // Ends the change at once (a new pick, or before the next swap): the new stack shows in full.
    stopMotion() {
      for (const anim of this.anims) anim.cancel();
      this.anims = [];
      for (const img of [this.front, this.back]) this.endDissolve(img);
      this.clearGhost();
    }

    clearGhost() {
      if (this.ghost) this.ghost.getContext('2d').clearRect(0, 0, this.ghost.width, this.ghost.height);
    }

    endDissolve(img) {
      img.classList.remove('is-entering', 'is-leaving');
      img.style.maskImage = img.style.webkitMaskImage = '';
    }

    // The new photo is masked to the area that changed and comes into focus there over the old one, so the face and
    // knit stay still. Without a mask (photos that cannot be read) it is a plain crossfade.
    dissolve(next, old) {
      const mask = this.changeMask(next, old);
      next.classList.add('is-entering');
      old.classList.add('is-leaving');
      if (mask) next.style.maskImage = next.style.webkitMaskImage = `url(${mask})`;
      const anim = this.play(next, [
        { opacity: 0, filter: mask ? 'blur(4px)' : 'blur(0px)' },
        { opacity: 1, filter: 'blur(0px)' }
      ], { duration: SWAP_MS, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' });
      anim.onfinish = () => {
        if (!this.anims.includes(anim)) return; // already ended by stopMotion
        this.endDissolve(next);
        this.endDissolve(old);
        anim.cancel();
      };
    }

    // Where two photos differ (their chains): compared at 90 x 120, grown by 2 px so the soft new chain is not
    // clipped, feathered, and returned as a PNG mask in the photo's 3:4 frame (the CSS sizes and places it like the
    // photo). Null when the pixels cannot be read (a cross-origin image).
    changeMask(a, b) {
      const w = 90, h = 120, n = w * h;
      try {
        const c = this.scratch || (this.scratch = Object.assign(win.document.createElement('canvas'), { width: w, height: h * 2 }));
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.clearRect(0, 0, w, h * 2);
        ctx.drawImage(a, 0, 0, w, h);
        ctx.drawImage(b, 0, h, w, h);
        const px = ctx.getImageData(0, 0, w, h * 2).data;
        const hit = new Uint8Array(n);
        for (let i = 0; i < n; i++) {
          const p = i * 4, q = p + n * 4;
          hit[i] = Math.abs(px[p] - px[q]) + Math.abs(px[p + 1] - px[q + 1]) + Math.abs(px[p + 2] - px[q + 2]) > 36 ? 1 : 0;
        }
        const out = ctx.createImageData(w, h);
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let on = 0;
            for (let dy = -2; dy <= 2 && !on; dy++) {
              for (let dx = -2; dx <= 2; dx++) {
                const yy = y + dy, xx = x + dx;
                if (yy >= 0 && yy < h && xx >= 0 && xx < w && hit[yy * w + xx]) { on = 1; break; }
              }
            }
            out.data[(y * w + x) * 4 + 3] = on ? 255 : 0;
          }
        }
        ctx.putImageData(out, 0, 0);
        const m = this.maskCanvas || (this.maskCanvas = Object.assign(win.document.createElement('canvas'), { width: w, height: h }));
        const mctx = m.getContext('2d');
        mctx.clearRect(0, 0, w, h);
        mctx.filter = 'blur(1.5px)';
        mctx.drawImage(c, 0, 0, w, h, 0, 0, w, h);
        return m.toDataURL();
      } catch {
        return null;
      }
    }

    // The old pendant fades on a copy of the canvas while the new one comes into focus.
    dissolvePendant(reg) {
      if (!this.ghost) {
        this.ghost = this.canvas.cloneNode();
        this.ghost.removeAttribute('data-sb-canvas');
        this.canvas.before(this.ghost);
      }
      const g = this.ghost.getContext('2d');
      g.clearRect(0, 0, this.ghost.width, this.ghost.height);
      g.drawImage(this.canvas, 0, 0);
      this.draw(reg);
      const fade = this.play(this.ghost, [{ opacity: 1 }, { opacity: 0 }], { duration: SWAP_MS, easing: 'ease-out', fill: 'forwards' });
      fade.onfinish = () => {
        if (!this.anims.includes(fade)) return; // already ended by stopMotion
        this.clearGhost();
        fade.cancel();
      };
      this.play(this.canvas, [{ opacity: 0, filter: 'blur(3px)' }, { opacity: 1, filter: 'blur(0px)' }], { duration: SWAP_MS, easing: 'cubic-bezier(.2,.7,.2,1)' });
    }

    draw(reg) {
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      if (!reg) return;
      const [ax, ay] = this.data.anchors[this.state.len];
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,.4)'; ctx.shadowBlur = 4; ctx.shadowOffsetX = 2; ctx.shadowOffsetY = 4;
      ctx.drawImage(reg.canvas, ax * SIZE - reg.pivot[0], ay * SIZE - reg.pivot[1]);
      ctx.restore();
    }

    // When idle: the photos one tap away at the width the stage uses, then this finish's pendant cut-outs.
    prefetch() {
      const dpr = win.devicePixelRatio || 1;
      const width = pickWidth(this.width * dpr, this.data.widths), index = this.data.widths.indexOf(width);
      for (const key of neighbours(this.state, this.data)) {
        const url = this.data.photos[key][index];
        if (this.fetched.has(url)) continue;
        this.fetched.add(url);
        Object.assign(new win.Image(), { decoding: 'async', src: url });
      }
      for (const id of Object.keys(this.data.pendants)) {
        if (id.startsWith(`${this.state.fin}|`)) this.pendantLayer(this.state.fin, id.split('|')[1]).catch(() => {});
      }
    }

    setStatus(text) {
      const status = this.querySelector('[data-sb-status]');
      if (status && status.textContent !== text) status.textContent = text;
    }

    // Share this stack: the device's share sheet where there is one (phones), else the link goes to the clipboard.
    // The link always carries the stack (sb_*), even before the first pick writes it into the address bar.
    async share() {
      const out = this.querySelector('[data-sb-copied]');
      const url = `${win.location.origin}${win.location.pathname}${stateQuery(this.state, win.location.search)}${win.location.hash}`;
      if (win.navigator.share) {
        try {
          await win.navigator.share({ title: win.document.title, text: this.captionText, url });
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return; // the sheet was closed
        }
      }
      try {
        await win.navigator.clipboard.writeText(url);
        out.textContent = this.s.copied;
      } catch {
        out.textContent = this.s.copy_failed;
      }
      win.clearTimeout(this.copyTimer);
      this.copyTimer = win.setTimeout(() => { out.textContent = ''; }, 2500);
    }

    // Phones: the pinned close-up or the whole photo, fitted (still pinned).
    expand() {
      const button = this.querySelector('[data-sb-expand]');
      const full = this.media.classList.toggle('is-full');
      button.setAttribute('aria-expanded', String(full));
      button.textContent = full ? this.s.view_crop : this.s.view_full;
      this.updateBar(); // the taller photo can cover the button under the picks
    }

    // Phones and tablets: the add button floats at the bottom once the shopper is past the heading, whenever the one
    // under the picks is out of sight: still below the screen, or scrolled up under the pinned photo. None at 1000px
    // and up (the CSS hides the bar there; the state stays off too, so a rotated tablet starts clean).
    updateBar() {
      if (!this.bar || !this.buy) return;
      const button = this.buy.querySelector('[data-sb-add]').getBoundingClientRect();
      const cover = this.media.getBoundingClientRect().bottom;
      const inView = button.top < win.innerHeight && button.bottom > cover;
      const phone = win.matchMedia('(max-width: 999.98px)').matches;
      const show = phone && this.getBoundingClientRect().top < -120 && !inView;
      if (show === this.classList.contains('has-bar')) return;
      this.classList.toggle('has-bar', show);
      win.document.documentElement.classList.toggle('hb-sb-bar-on', show);
      this.bar.setAttribute('aria-hidden', String(!show));
      for (const el of this.bar.querySelectorAll('button')) el.tabIndex = show ? 0 : -1;
    }
  }

  win.customElements.define('humbler-stack-builder', StackBuilder);
  return api;
});
